import { useEffect, useState, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getProfile, updateProfile, imageUploadUrl, setProfileImage, removeProfileImage, PROFILE_TYPE_LABELS, type ProfileType } from "../lib/profiles";
import { uploadToSignedUrl } from "../lib/verification";
import { Avatar } from "../components/Avatar";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { EditProfileRail } from "../components/EditProfileRail";

const inputClass = "rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink";
const labelClass = "grid gap-1 text-xs font-bold text-muted";

export function EditProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh } = useProfiles();

  const [type, setType] = useState<ProfileType | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [headline, setHeadline] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [ownerOperator, setOwnerOperator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);

  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !id) return;
    if (file.size > 5 * 1024 * 1024) { setError("Image must be under 5 MB."); return; }
    const ext = (file.name.split(".").pop() ?? "png").toLowerCase();
    setImgBusy(true);
    setError(null);
    try {
      const { upload_url, path } = await imageUploadUrl(id, ext);
      await uploadToSignedUrl(upload_url, file);
      const { image_url } = await setProfileImage(id, path);
      setImageUrl(`${image_url}?t=${Date.now()}`); // cache-bust the preview
      await refresh();
    } catch {
      setError("Could not upload the image.");
    } finally {
      setImgBusy(false);
    }
  }
  async function onRemoveImage() {
    if (!id) return;
    setImgBusy(true);
    try {
      await removeProfileImage(id);
      setImageUrl(null);
      await refresh();
    } finally {
      setImgBusy(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    getProfile(id)
      .then(({ profile, detail }) => {
        setType(profile.type);
        setDisplayName(profile.display_name ?? "");
        setHeadline(profile.headline ?? "");
        setImageUrl(profile.image_url ?? null);
        const d = (detail ?? {}) as Record<string, unknown>;
        const arr = (v: unknown) => (Array.isArray(v) ? v.join(", ") : "");
        setFields({
          cdl_class: String(d.cdl_class ?? ""),
          years_experience: d.years_experience != null ? String(d.years_experience) : "",
          equipment: arr(d.equipment),
          legal_name: String(d.legal_name ?? ""),
          dot_number: String(d.dot_number ?? ""),
          mc_number: String(d.mc_number ?? ""),
          fleet_size: d.fleet_size != null ? String(d.fleet_size) : "",
          service_areas: arr(d.service_areas),
          agency: String(d.agency ?? ""),
          specialties: arr(d.specialties),
          authority_mc: String(d.authority_mc ?? ""),
          freight_types: arr(d.freight_types),
        });
        setOwnerOperator(Boolean(d.owner_operator));
      })
      .catch(() => setError("Could not load the profile"))
      .finally(() => setLoading(false));
  }, [id]);

  const set = (k: string) => (e: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [k]: e.target.value }));
  const num = (v?: string) => (v ? Number(v) : undefined);
  const list = (v?: string) => (v ?? "").split(",").map((s) => s.trim()).filter(Boolean);

  function detailFor(): Record<string, unknown> {
    switch (type) {
      case "driver":
        return { cdl_class: fields.cdl_class || undefined, years_experience: num(fields.years_experience), owner_operator: ownerOperator, equipment: list(fields.equipment) };
      case "company":
        return { legal_name: fields.legal_name || undefined, dot_number: fields.dot_number || undefined, mc_number: fields.mc_number || undefined, fleet_size: num(fields.fleet_size), service_areas: list(fields.service_areas) };
      case "recruiter":
        return { agency: fields.agency || undefined, specialties: list(fields.specialties) };
      case "broker_dispatcher":
        return { authority_mc: fields.authority_mc || undefined, freight_types: list(fields.freight_types) };
      default:
        return {};
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await updateProfile({ id, display_name: displayName, headline, detail: detailFor() });
      await refresh();
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="p-10 font-mono text-sm text-muted">Loading…</p>;

  return (
    <div className="grid gap-6">
      <PageHeader title={`Edit ${type ? PROFILE_TYPE_LABELS[type].toLowerCase() : ""} profile`} subtitle="Update your profile details." />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
      <form className="grid gap-3" onSubmit={onSubmit}>
        <div className="flex items-center gap-4">
          <Avatar src={imageUrl} name={displayName || (type ? PROFILE_TYPE_LABELS[type] : "?")} className="h-16 w-16 rounded-xl text-2xl" />
          <div className="grid gap-1.5">
            <span className={labelClass}>{type === "company" || type === "broker_dispatcher" ? "Logo" : "Photo"}</span>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer rounded-lg border border-line px-3 py-1.5 text-sm font-bold text-ink transition-colors hover:border-accent">
                {imgBusy ? "Uploading…" : imageUrl ? "Change" : "Upload"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPickImage} disabled={imgBusy} />
              </label>
              {imageUrl && <button type="button" className="text-sm font-bold text-danger" onClick={onRemoveImage} disabled={imgBusy}>Remove</button>}
            </div>
          </div>
        </div>
        <label className={labelClass}>
          Display name
          <input className={inputClass} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label className={labelClass}>
          Headline
          <input className={inputClass} value={headline} onChange={(e) => setHeadline(e.target.value)} />
        </label>

        {type === "driver" && (
          <>
            <label className={labelClass}>
              CDL class
              <select className={inputClass} value={fields.cdl_class} onChange={set("cdl_class")}>
                <option value="">—</option>
                <option>A</option>
                <option>B</option>
                <option>C</option>
              </select>
            </label>
            <label className={labelClass}>
              Years of experience
              <input className={inputClass} type="number" value={fields.years_experience} onChange={set("years_experience")} />
            </label>
            <label className="flex items-center gap-2 text-sm font-bold">
              <input type="checkbox" checked={ownerOperator} onChange={(e) => setOwnerOperator(e.target.checked)} />
              Owner-operator
            </label>
            <label className={labelClass}>
              Equipment (comma-separated)
              <input className={inputClass} value={fields.equipment} onChange={set("equipment")} />
            </label>
          </>
        )}
        {type === "company" && (
          <>
            <label className={labelClass}>
              Legal name
              <input className={inputClass} value={fields.legal_name} onChange={set("legal_name")} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className={labelClass}>
                DOT number
                <input className={inputClass} value={fields.dot_number} onChange={set("dot_number")} />
              </label>
              <label className={labelClass}>
                MC number
                <input className={inputClass} value={fields.mc_number} onChange={set("mc_number")} />
              </label>
            </div>
            <label className={labelClass}>
              Fleet size
              <input className={inputClass} type="number" value={fields.fleet_size} onChange={set("fleet_size")} />
            </label>
            <label className={labelClass}>
              Service areas (comma-separated)
              <input className={inputClass} value={fields.service_areas} onChange={set("service_areas")} />
            </label>
          </>
        )}
        {type === "recruiter" && (
          <>
            <label className={labelClass}>
              Agency
              <input className={inputClass} value={fields.agency} onChange={set("agency")} />
            </label>
            <label className={labelClass}>
              Specialties (comma-separated)
              <input className={inputClass} value={fields.specialties} onChange={set("specialties")} />
            </label>
          </>
        )}
        {type === "broker_dispatcher" && (
          <>
            <label className={labelClass}>
              Broker authority / MC
              <input className={inputClass} value={fields.authority_mc} onChange={set("authority_mc")} />
            </label>
            <label className={labelClass}>
              Freight types (comma-separated)
              <input className={inputClass} value={fields.freight_types} onChange={set("freight_types")} />
            </label>
          </>
        )}

        {error && <p className="font-mono text-sm text-danger">{error}</p>}
        <div className="flex gap-3">
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save changes"}
          </Button>
          <Link to="/" className="self-center text-sm font-bold text-muted">
            Cancel
          </Link>
        </div>
      </form>
      {type && (
        <EditProfileRail profileId={id ?? ""} type={type} displayName={displayName} headline={headline} fields={fields} ownerOperator={ownerOperator} />
      )}
      </div>
    </div>
  );
}
